<script setup lang="ts">
import { IonButton, IonInput, useIonRouter } from '@ionic/vue'
import { inject, onMounted, onUpdated, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useElementSize } from '@vueuse/core'
import { SafeArea } from 'capacitor-plugin-safe-area'
import ShareButton from './ShareButton.vue'
import FavouriteButton from './FavouriteButton.vue'
import SendIcon from '@/components/svg/SendIcon.vue'
import TextBubble from '@/components/TextBubble.vue'
import type { Chat, Message, StarOperation } from '@/types'
import { useChatsStore } from '@/store/chats'
import { useKeyboardStore } from '@/store/keyboard'
import { useUserStore } from '@/store/user'
import { useAuthStore } from '@/store/auth'
import ChapterPlot from '@/views/chat/ChapterPlot.vue'
import type { CreatedEventModel } from '@/types/universe'
import { useGlobalStateStore } from '@/store/globalState'
import { usePreferencesStore } from '@/store/preferences'
import { useShareStore } from '@/store/shareStore'
import { autoPlayKey } from '@/constant/symbol'

interface Props {
  chat: Chat | undefined
  personCountIsHidden?: boolean
  inviteIsHidden?: boolean
  disabled?: boolean
  chatRoundEnd?: boolean
  isPreview: boolean
  messages: Message[]
}

interface Emits {
  (e: 'click:star', operation: StarOperation): void
  (e: 'sendMessage', message: string): void
  (e: 'updateInputHeight', height: number): void
  (e: 'updateEvent', event: CreatedEventModel): void
  (e: 'onToggleDecorations', show: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const router = useIonRouter()
const keyboardStore = useKeyboardStore()
const chatsStore = useChatsStore()
const userStore = useUserStore()
const globalStore = useGlobalStateStore()
const shareStore = useShareStore()
const { openModal } = useAuthStore()

const { logoutChatInput } = storeToRefs(chatsStore)
// the user input value
const userInput = ref('')
// the user input area UI dom
const inputRef = ref()
// the full area dom UI
const messageAreaEl = ref<HTMLElement | null>(null)
const { height: messageAreaHeight } = useElementSize(messageAreaEl)

// This variable control the suggest response page show or not
const showEventSuggestions = ref<boolean>(false)
const autoplay = inject(autoPlayKey)
const manuallyAutoplay = ref(false)
// When the page updates, check the logoutChatInput value
// If it is not empty, assign to userInput
// If it is empty, do nothing
onMounted(() => {
  if (logoutChatInput.value !== '') {
    userInput.value = logoutChatInput.value
    logoutChatInput.value = ''
  }
})

onUpdated(() => {
  if (logoutChatInput.value !== '') {
    userInput.value = logoutChatInput.value
    logoutChatInput.value = ''
  }
})

watch(
  () => messageAreaHeight.value,
  async (newHeight) => {
    const safeInsets = await SafeArea.getSafeAreaInsets()
    emit('updateInputHeight', newHeight + safeInsets.insets.bottom)
  },
)

watch(
  () => showEventSuggestions.value,
  (val) => {
    globalStore.setDisableSwipe(val)
    emit('onToggleDecorations', !val)
  },
)

watch(
  () => props.chatRoundEnd,
  (newVal) => {
    console.debug(`chatRoundEnd: ${newVal}`)
    const lastEvent = (props.chat?.universe.events.length || 0) >= 4
    if (lastEvent) {
      showEventSuggestions.value = false
      return
    }
    if (newVal && chatsStore.currentChatId === props.chat?.chatId) {
      showScenarioSheet()
    }
  },
)

watch(
  () => userStore.isLoggedIn,
  (newVal) => {
    if (
      newVal
      && props.chatRoundEnd
      && props.chat?.chatId === chatsStore.currentChatId
    ) {
      showEventSuggestions.value = true
    }
  },
)

function handleSendClick() {
  // Make sure the event happened after keyboard is hidden
  setTimeout(() => {
    console.debug('handleSendClick')
    showEventSuggestions.value = false
    if (userInput.value === '') {
      return
    }
    emit('sendMessage', userInput.value)
    userInput.value = ''
  }, 300)
}

function showScenarioSheet() {
  if (userStore.isLoggedIn) {
    if ((props.chat?.universe.events.length || 0) >= 4) {
      return
    }
    showEventSuggestions.value = true
  }
  else {
    openModal()
  }
}
const showTipBubble = ref(false)
const preferenceStore = usePreferencesStore()
watch(
  () => preferenceStore.preferencesAreLoaded,
  (newVal) => {
    if (newVal) {
      if (preferenceStore.showedReplyTip) {
        return
      }
      setTimeout(() => {
        showTipBubble.value = true
        preferenceStore.showedReplyTip = true
        setTimeout(() => {
          showTipBubble.value = false
        }, 5000)
      }, 3000)
    }
  },
)

function openCreatorProfile() {
  const creatorId = props.chat?.universe.creatorUserId
  console.debug(`creatorId: ${creatorId}`)
  if (creatorId) {
    router.push(`/profile?userId=${creatorId}`)
  }
}

defineExpose({
  showScenarioSheet,
})
</script>

<template>
  <div
    :class="`${
      showEventSuggestions
        ? 'flex flex-col h-full responsing mt-unsafe-area pt-22 z-301'
        : ''
    } flex-shrink-0 gradient-bg z-100`"
  >
    <chapter-plot
      :open="showEventSuggestions"
      :chat="chat!"
      :messages="messages"
      :is-preview="isPreview"
      @close="
        (event) => {
          if (event) {
            // put the new event in the universe of the chat
            emit('updateEvent', event);
          }
          showEventSuggestions = false;
        }
      "
    />
    <div
      v-if="!showEventSuggestions"
      id="sekai-message-input"
      ref="messageAreaEl"
      class="text-on-bg-1 flex flex-col"
    >
      <div v-if="!keyboardStore.isTyping" class="flex flex-col px-4 pt-2">
        <p
          class="text-3.5 font-600 leading-6"
          @click.stop="() => {
            openCreatorProfile()
          }"
        >
          {{ chat?.universe.creatorName }}
        </p>
      </div>
      <div v-else class="flex items-center px-4 py-2">
        <p class="color-#FFE86E text-4 fw-600">
          {{ chat?.userChar?.charName ?? "Narrator" }}
        </p>
      </div>
      <div class="py-2 pl-4 pr-2 flex items-center">
        <div
          :class="`w-full px-4 rounded-full border-1 border-style-solid
            ${
            chatRoundEnd
              ? 'input-bg border-color-#05F0FF'
              : 'input-bg-2 border-color-transparent'
          }
          `"
        >
          <text-bubble
            v-if="showTipBubble"
            :chat-round-end="chatRoundEnd || false"
            text="Tap here to interact"
            position="bottom-right"
          />
          <div
            v-if="chatRoundEnd"
            class="py-2"
            @click="
              () => {
                showScenarioSheet();
              }
            "
          >
            Select Scenario
          </div>
          <ion-input
            v-else
            ref="inputRef"
            v-model="userInput"
            class="custom"
            :disabled="disabled"
            autocomplete="on"
            autocorrect="on"
            autocapitalize="on"
            :placeholder="$t('messageInput.placeholder')"
            @ion-focus="
              () => {
                if (!userStore.isLoggedIn) {
                  keyboardStore.hideKeyboard();
                  openModal();
                }
                console.debug(`ion-focus: ${autoplay}`);
                // 如果这个时候autoplay已经是true了，说明是用户手动点击的，不需要再次触发
                // 但我们要把手动autoplay的标记为true
                if (!autoplay) {
                  manuallyAutoplay = true;
                }
                else {
                  //如果这个autoplay是true，自动播放状态，那么焦点到输入框的时候，我们要关闭autoplay
                  manuallyAutoplay = false;
                  autoplay = false;
                }
              }
            "
            @ion-blur="
              () => {
                //如果不是手动autoplay暂停，那么就blur的时候重新打开autoplay
                if (manuallyAutoplay) {
                  manuallyAutoplay = false;
                }
                else {
                  autoplay = false;
                }
              }
            "
          />
        </div>
        <ion-button
          v-if="userInput !== ''"
          size="small"
          fill="clear"
          @pointerdown="handleSendClick"
        >
          <send-icon />
        </ion-button>
        <favourite-button
          v-if="!isPreview && !keyboardStore.isTyping"
          :chat="chat!"
          :is-favorite="chat?.isStared || false"
        />
        <share-button
          v-if="!isPreview && !keyboardStore.isTyping"
          :chat="chat!"
          @on-create-video="() => {
            shareStore.setSharingInfo(
              props.chat!,
              props.messages,
            )
            router.push('/select-msg')
          }"
        />
      </div>
    </div>
    <div class="pb-unsafe-area" />
  </div>
</template>

<style scoped>
.gradient-bg {
  background: linear-gradient(
    180deg,
    rgba(47, 47, 47, 0.35) 0%,
    rgba(47, 47, 47, 0.35) 27.85%,
    rgba(47, 47, 47, 0.35) 100%
  );
}
.responsing {
  background: rgba(41, 41, 41, 0.5);
  backdrop-filter: blur(12.5px);
}

.tags {
  max-width: 70%;
}
ion-input.custom {
  --color: #fff;
}

.input-bg {
  background: rgba(0, 0, 0, 0.48);
}
.input-bg-2 {
  background: rgba(0, 0, 0, 0.24);
}
</style>
